// Tracking-snippet shapes and defaults. Client-safe (no server imports) so the
// /admin/settings form can import it; the DB read/write lives in lib/trackingDb.
//
// Every third-party tag the site renders is configured here, so the ids can be
// changed in the admin panel rather than in code. They used to be NEXT_PUBLIC_*
// environment variables, which are baked into the bundle at BUILD time — so
// changing one meant editing .env.production on the server and rebuilding, and
// an id that was never set looked identical to a tag that was broken. The env
// vars are still read, but only as the default when the admin has nothing
// saved, so an existing deployment keeps behaving exactly as it did.

/** Apollo.io website tracker — the account this site was set up with. */
export const APOLLO_DEFAULT_APP_ID = "6a01e56ac49ec10011bf0a43";

/** Google Tag Manager container. */
export const GTM_DEFAULT_ID = "GTM-KKPPNTN9";

export type TrackingSettings = {
  /** Google Tag Manager container id (GTM-XXXXXXX). */
  gtmId: string;
  gtmEnabled: boolean;
  /** GA4 Measurement ID (G-XXXXXXXXXX) loaded directly, bypassing GTM.
   *  Usually empty: GA is normally wired inside the GTM container instead. */
  ga4Id: string;
  ga4Enabled: boolean;
  /** Microsoft Clarity project id. */
  clarityId: string;
  clarityEnabled: boolean;
  /** Apollo.io app id; the tracker is skipped when this is empty. */
  apolloAppId: string;
  apolloEnabled: boolean;
  /** Free-form snippets for anything else (verification tags, pixels…). */
  headHtml: string;
  bodyHtml: string;
};

/** What the site renders when the DB is unreachable: the ids that were in the
 *  environment before any of this was editable, so a DB outage degrades to the
 *  previous behaviour rather than to no analytics at all. */
export const DEFAULT_TRACKING: TrackingSettings = {
  gtmId: process.env.NEXT_PUBLIC_GTM_ID || GTM_DEFAULT_ID,
  gtmEnabled: true,
  ga4Id: process.env.NEXT_PUBLIC_GA_ID || "",
  ga4Enabled: true,
  clarityId: process.env.NEXT_PUBLIC_CLARITY_ID || "",
  clarityEnabled: true,
  apolloAppId: APOLLO_DEFAULT_APP_ID,
  apolloEnabled: true,
  headHtml: "",
  bodyHtml: "",
};

/**
 * The Apollo loader, verbatim from Apollo's install instructions: it injects
 * their tracker into <head> itself, so where this runs on the page is
 * irrelevant. `appId` is interpolated as a JSON string.
 */
export const apolloSnippet = (appId: string) =>
  `function initApollo(){var n=Math.random().toString(36).substring(7),o=document.createElement("script");` +
  `o.src="https://assets.apollo.io/micro/website-tracker/tracker.iife.js?nocache="+n,o.async=!0,o.defer=!0,` +
  `o.onload=function(){window.trackingFunctions.onLoad({appId:${JSON.stringify(appId)}})},` +
  `document.head.appendChild(o)}initApollo();`;

/** Microsoft Clarity, verbatim from Clarity's install screen. */
export const claritySnippet = (id: string) =>
  `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};` +
  `t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;` +
  `y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})` +
  `(window, document, "clarity", "script", ${JSON.stringify(id)});`;

/** The GTM loader, verbatim from Google's install instructions. */
export const gtmSnippet = (id: string) =>
  `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer',${JSON.stringify(id)});`;
